import React from "react";
import { ColumnDef } from "@components/spreadsheet/types";
import { BaseCellProps, CellType } from "@components/spreadsheet/cells";
import { Account } from "@common/types/domains/accounting/account";
import { ClassWithChildren } from "@helpers/class";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { currencyFormatter } from "@common/helpers/currency";
import { DiffCell } from "../cells/DiffCell";
import { AmountDiffCell } from "../cells/AmountDiffCell";
import { ClassDiffCell } from "../cells/ClassDiffCell";

interface JournalEntryLinesColumnsParams {
    accounts: Account[];
    counterpartiesMap: Map<string, string>;
    classesMap: Map<string, ClassWithChildren>;
    hasAnyClass: boolean;
}
const GRID_TEMPLATE_COLUMNS_VARIANTS = {
    id: "40fr",
    accountId: "429fr",
    counterpartyId: "150fr",
    classIds: "100fr",
    debitAmount: "122fr",
    creditAmount: "122fr",
    description: "434fr",
} as const;

export const GRID_TEMPLATE_COLUMNS = Object.values(
    GRID_TEMPLATE_COLUMNS_VARIANTS,
).join(" ");

export const GRID_TEMPLATE_COLUMNS_NO_CLASS = [
    GRID_TEMPLATE_COLUMNS_VARIANTS.id,
    GRID_TEMPLATE_COLUMNS_VARIANTS.accountId,
    GRID_TEMPLATE_COLUMNS_VARIANTS.counterpartyId,
    GRID_TEMPLATE_COLUMNS_VARIANTS.debitAmount,
    GRID_TEMPLATE_COLUMNS_VARIANTS.creditAmount,
    GRID_TEMPLATE_COLUMNS_VARIANTS.description,
].join(" ");

export function getJournalEntryLinesColumns({
    accounts,
    counterpartiesMap,
    classesMap,
    hasAnyClass,
}: JournalEntryLinesColumnsParams): ColumnDef[] {
    return [
        { field: "", label: "", cellType: CellType.ROW_NUMBER },
        {
            field: "accountId",
            label: "Account",
            cellType: CellType.ACCOUNT_SEARCH,
            accounts,
            aggregation: () => "Total",
            cellFn: ({ row }) =>
                React.createElement(DiffCell, {
                    oldValue: row.oldAccountName,
                    newValue: row.accountName ?? null,
                    hasChanges: row.hasChanges,
                    isAdded: row.isAdded,
                    isRemoved: row.isRemoved,
                    copyId: row.accountId,
                    copyLabel: "Account",
                }),
        },
        {
            field: "counterpartyId",
            label: "Counterparty",
            cellType: CellType.COUNTERPARTY,
            counterpartiesMap,
            cellFn: ({ row }) =>
                React.createElement(DiffCell, {
                    oldValue: row.oldCounterpartyName,
                    newValue: row.counterpartyName ?? null,
                    hasChanges: row.hasChanges,
                    isAdded: row.isAdded,
                    isRemoved: row.isRemoved,
                    copyId: row.counterpartyId,
                    copyLabel: "Counterparty",
                }),
        },
        ...(hasAnyClass
            ? [
                  {
                      field: "classIds",
                      label: "Class",
                      cellType: CellType.CLASS,
                      cellFn: (props: BaseCellProps) =>
                          React.createElement(ClassDiffCell, {
                              classIds: props.row.classIds ?? [],
                              oldClassIds: props.row.oldClassIds,
                              classesMap,
                              hasChanges: props.row.hasChanges,
                              isAdded: props.row.isAdded,
                              isRemoved: props.row.isRemoved,
                          }),
                  },
              ]
            : []),
        {
            field: "debitAmount",
            label: "Debit",
            cellType: CellType.MATH,
            isMonetary: true,
            aggregation: (data) =>
                currencyFormatter.format(
                    sumMonetaryValues(
                        data.map(({ debitAmount }) => debitAmount ?? 0),
                    ),
                ),
            cellFn: ({ row }) =>
                React.createElement(AmountDiffCell, {
                    oldAmount: row.oldDebitAmount,
                    newAmount: row.debitAmount,
                    hasChanges: row.hasChanges,
                    isAdded: row.isAdded,
                    isRemoved: row.isRemoved,
                }),
        },
        {
            field: "creditAmount",
            label: "Credit",
            cellType: CellType.MATH,
            isMonetary: true,
            aggregation: (data) =>
                currencyFormatter.format(
                    sumMonetaryValues(
                        data.map(({ creditAmount }) => creditAmount ?? 0),
                    ),
                ),
            cellFn: ({ row }) =>
                React.createElement(AmountDiffCell, {
                    oldAmount: row.oldCreditAmount,
                    newAmount: row.creditAmount,
                    hasChanges: row.hasChanges,
                    isAdded: row.isAdded,
                    isRemoved: row.isRemoved,
                }),
        },
        {
            field: "description",
            label: "Description",
            cellType: CellType.STRING,
            cellFn: ({ row }) =>
                React.createElement(DiffCell, {
                    oldValue: row.oldDescription,
                    newValue: row.description ?? null,
                    hasChanges: row.hasChanges,
                    isAdded: row.isAdded,
                    isRemoved: row.isRemoved,
                }),
        },
    ];
}
